/**
 * The client's chat screen, drawn for the Mini App's `/chat` and the phone website's messages
 * alike (shared-client-screens D2, D3; rows M1 to M24). Props only: the app's ClientChatScreen
 * and the website's PhoneMessages are its data parts, and the differences between them are the
 * ruled slots below. Visual authority: docs/mockups/client-chat-relay/client-chat-thread.html.
 *
 * - Header: the flow back button (`onBack`: the app's own return, the browser's Back on the
 *   website, M2), the studio's name and the sub-line (`subline`: the app's room line, or the
 *   website's Secure · Telegram, M3).
 * - The room switch inside the fold that slides up under the header while the keyboard is open
 *   (`roomSwitch`: the app's Secure and Telegram tabs, or the website's filter row, M4 to M6);
 *   the app's History lenses under it (`lensRow`). Choosing either opens the new list at its newest.
 * - The thread: `status` (loading, not-found, error with Try again, M10), the ruled empty state
 *   (`empty`, rule 8), the timeline (`markOf` draws the website's room marks, M7; `openLink`
 *   opens a notice's links, M20; `onDoor` gets the place the client was reading, M21, M22) and,
 *   with `focusId`, the message a banner opened at (M19).
 * - The composer (`composer`, null while read-only), the studio's typing line (`typing`, M12),
 *   with "the business" when the studio has no name (M13).
 * - Owned here so the two never drift (M14 to M18, M23; Gabriel 2026-10-06): the thread's scroll
 *   (useChatThreadScroll: pinned to the newest, older history four screens early, no spinner for
 *   it), the send's landing (useSendLanding), the keyboard rails (the in-flow spacer with the
 *   admin's 8px above the keyboard, the native keyboard scroll held off, the viewport variables),
 *   the mount scroll reset and the scroll lock.
 *
 * Not here (D2): Telegram's safe area and the `data-client-theme` effect stay in the app's data
 * part; the website's server writes that attribute and a view removing it would strip its theme (M24).
 */
import { useCallback, useLayoutEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
import type { ClientDoor, ClientThreadEntry, ThreadRoom } from '@telify/shared/browser';
import { FlowBackButton } from '../../components/booking/FlowBackButton';
import type { SendOrigin } from '../../components/chat/sendLanding';
import { SlidingTabIndicator } from '../../components/chat/SlidingTabIndicator';
import { useSendLanding } from '../../components/chat/useSendLanding';
import { useClientLock } from '../../hooks/useClientLock';
import { useChatThreadScroll, type ChatThreadGate, type SavedPlace } from '../../hooks/chat/useChatThreadScroll';
import { useIsKeyboardOpen } from '../../hooks/useIsKeyboardOpen';
import { useKeyboardSpacerFollow } from '../../hooks/useKeyboardSpacerFollow';
import { usePreventNativeKeyboardScroll } from '../../hooks/usePreventNativeKeyboardScroll';
import { useVisualViewportCssVars } from '../../hooks/useVisualViewportCssVars';
import { CHAT_NOT_FOUND_LINE, type ClientSendIssue } from '../../lib/client/clientChatThread';
import { ClientChatTimeline, type ChatMark } from './ClientChatTimeline';
import { SecureComposer } from './SecureComposer';
import styles from './ClientChatScreen.module.css';

/** Extra room above the keyboard, the admin composer's value (Gabriel 2026-10-04: "make it same as admin, 8"). */
export const KEYBOARD_EXTRA_PX = 8;

export type ClientChatStatus = 'loading' | 'ready' | 'not-found' | 'error';

/** The line under the name: the app's Secure room, its Telegram history, or the website's two rooms. */
export type ChatSubline = 'secure' | 'history' | 'rooms';

export interface ChatChip<Id extends string> {
  readonly id: Id;
  readonly label: string;
  readonly current: boolean;
}

export interface ChatChipRow<Id extends string> {
  /** The row's accessible name. */
  readonly label: string;
  readonly chips: readonly ChatChip<Id>[];
  readonly onChoose: (id: Id) => void;
}

/** What sits in the fold under the header: the app's two room tabs, or the website's filter row. */
export type ChatRoomSwitch<Lens extends string> =
  | { readonly kind: 'tabs'; readonly room: ThreadRoom; readonly secureCount: number; readonly onChoose: (room: ThreadRoom) => void }
  | ({ readonly kind: 'chips' } & ChatChipRow<Lens>);

export interface ChatComposer {
  /** The draft is kept for the browser session under this name. */
  readonly draftScope: string;
  readonly placeholder: string;
  /** Shows the bubble and sends it; returns the bubble's key. */
  readonly onSend: (text: string) => string;
  /** A keystroke with words in the pill. */
  readonly onType?: () => void;
}

export interface ClientChatViewProps<Lens extends string> {
  /** The studio's name. */
  readonly title: string;
  readonly subline: ChatSubline;
  readonly roomSwitch: ChatRoomSwitch<Lens>;
  /** The app's History lenses, under the fold. */
  readonly lensRow?: ChatChipRow<Lens> | null;
  /** No composer: the thread runs to the bottom edge. */
  readonly readOnly: boolean;
  readonly status: ClientChatStatus;
  readonly onRetry: () => void;
  /** The ruled empty state (chatEmptyState), or null. */
  readonly empty: { readonly kind: 'explainer' } | { readonly kind: 'line'; readonly text: string } | null;
  readonly entries: ClientThreadEntry[];
  readonly timezone: string;
  readonly secureOf: (entry: ClientThreadEntry) => boolean;
  readonly markOf?: (entry: ClientThreadEntry) => ChatMark;
  readonly issueOf: (entry: ClientThreadEntry) => ClientSendIssue | null;
  readonly onRetrySend: (key: string) => void;
  /** A notice's door, with the place the client was reading for the way back. */
  readonly onDoor: (door: ClientDoor, place: SavedPlace<Lens>) => void;
  readonly openLink: (href: string) => void;
  readonly composer: ChatComposer | null;
  /** The studio is typing, under a view that shows its secure messages. */
  readonly typing: boolean;
  /** The studio's name in the explainer and the typing line; "the business" when empty. */
  readonly name: string;
  readonly onBack: () => void;
  /** The rest gate the data part shares with the scroll (useChatThreadScroll). */
  readonly gate: ChatThreadGate;
  readonly hasOlder: boolean;
  readonly loadOlder: () => unknown;
  /** The room and lens on screen, recorded in a saved place. */
  readonly room: ThreadRoom;
  readonly lens: Lens;
  /** The place to come back to, read on the first layout. */
  readonly restore?: SavedPlace<Lens> | null;
  /** Open at this entry (a banner's) instead of the newest. */
  readonly focusId?: string | null;
  readonly testId?: string;
}

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const PlaneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4 20-7z" />
  </svg>
);

function Subline({ kind }: { kind: ChatSubline }) {
  if (kind === 'secure') return <><LockIcon />Secure messaging</>;
  if (kind === 'history') return <>Everything from your Telegram chat</>;
  return (
    <>
      <span className={styles.leg}><LockIcon />Secure</span>
      <span className={styles.dot} aria-hidden="true">·</span>
      <span className={styles.leg}><PlaneIcon />Telegram</span>
    </>
  );
}

function Chips<Id extends string>({ row, onChoose }: { row: ChatChipRow<Id>; onChoose: (id: Id) => void }) {
  return (
    <div className={styles.chips} role="group" aria-label={row.label}>
      {row.chips.map((chip) => (
        <button key={chip.id} type="button" className={styles.chip} aria-pressed={chip.current} onClick={() => onChoose(chip.id)}>
          {chip.label}
        </button>
      ))}
    </div>
  );
}

export function ClientChatView<Lens extends string>({
  title, subline, roomSwitch, lensRow, readOnly, status, onRetry, empty, entries, timezone, secureOf, markOf, issueOf,
  onRetrySend, onDoor, openLink, composer, typing, name, onBack, gate, hasOlder, loadOlder, room, lens, restore, focusId,
  testId = 'client-chat-screen',
}: ClientChatViewProps<Lens>) {
  const reducedMotion = useReducedMotion() === true;

  // ── The focused screen's rails and the keyboard rails ──
  useVisualViewportCssVars();
  usePreventNativeKeyboardScroll();
  useClientLock();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const pageRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  useKeyboardSpacerFollow(pageRef, spacerRef, { extraPx: KEYBOARD_EXTRA_PX });
  const keyboardOpen = useIsKeyboardOpen();

  // ── Scroll: pinned to the newest message, older history four screens early, the place held ──
  const {
    threadRef, innerRef, onScroll, pin, revealNewest, startNewList, savePlace,
  } = useChatThreadScroll({ gate, entries, hasOlder, loadOlder, room, lens, restore });

  // Opened at a message (a banner's): it comes into view once it is on screen.
  const focusedRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (!focusId || focusedRef.current === focusId) return;
    const thread = threadRef.current;
    const el = thread?.querySelector<HTMLElement>(`[data-entry-id="${CSS.escape(focusId)}"]`);
    if (!thread || !el) return;
    focusedRef.current = focusId;
    thread.scrollTop += el.getBoundingClientRect().top - thread.getBoundingClientRect().top - 24;
  }, [entries, focusId, threadRef]);

  const openDoor = useCallback((door: ClientDoor) => onDoor(door, savePlace()), [onDoor, savePlace]);

  // Another room, lens or filter: the new list opens at its newest entry.
  const chooseRoom = useCallback((next: ThreadRoom) => {
    if (roomSwitch.kind !== 'tabs' || next === roomSwitch.room) return;
    startNewList();
    roomSwitch.onChoose(next);
  }, [roomSwitch, startNewList]);
  const choose = useCallback((row: ChatChipRow<Lens>) => (id: Lens) => {
    if (row.chips.find((chip) => chip.current)?.id === id) return;
    startNewList();
    row.onChoose(id);
  }, [startNewList]);

  // ── Send: the bubble appears at once at the newest end, then the typed words travel into it ──
  const screenRef = useRef<HTMLDivElement>(null);
  const queueLanding = useSendLanding({
    stageRef: screenRef, tailRef: innerRef, entries, reduced: reducedMotion, ghostColor: 'var(--text-primary)',
  });
  const send = composer?.onSend;
  const sendMessage = useCallback((text: string, origin: SendOrigin | null) => {
    if (!send) return;
    pin();
    queueLanding(send(text), text, origin);
  }, [pin, queueLanding, send]);

  // The fold is an overlay over the thread's top edge (as the admin's room switcher): the screen
  // writes its height as --tabs-h, the thread reserves it as top padding, and raising the keyboard
  // slides it up under the header instead of collapsing the row (Gabriel 2026-10-06).
  const tabsRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const tabs = tabsRef.current;
    const stage = screenRef.current;
    if (!tabs || !stage) return;
    const write = () => stage.style.setProperty('--tabs-h', `${tabs.offsetHeight}px`);
    write();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(write);
    observer.observe(tabs);
    return () => observer.disconnect();
  }, []);

  const who = name || 'the business';
  const overlay = !readOnly;

  return (
    <div className={styles.page} ref={pageRef} data-testid={testId}>
      <div className={styles.atmosphere} aria-hidden="true" />
      <div className={`${styles.screen} ${readOnly ? styles.readOnly : ''}`} ref={screenRef}>
        <header className={styles.hdr}>
          <FlowBackButton onClick={onBack} />
          <div className={styles.who}>
            <div className={styles.whoText}>
              <h1 className={styles.whoName}>{title}</h1>
              <div className={styles.whoSub}><Subline kind={subline} /></div>
            </div>
          </div>
          <div />
        </header>

        <div className={`${styles.tabWrap} ${overlay ? styles.tabOverlay : ''}`} data-folded={keyboardOpen}>
          <div className={styles.tabSlide} ref={tabsRef}>
            {roomSwitch.kind === 'tabs' ? (
              <div className={styles.tabSegmented} role="tablist" aria-label="Chat">
                {/* The admin room switcher's motion (Gabriel 2026-10-06): the block's edges stretch apart and close. */}
                <SlidingTabIndicator index={roomSwitch.room === 'secure' ? 0 : 1} className={styles.tabIndicator} pad={3} gap={3} />
                <button
                  type="button"
                  role="tab"
                  aria-selected={roomSwitch.room === 'secure'}
                  className={`${styles.tabSegment} ${roomSwitch.room === 'secure' ? styles.active : ''}`}
                  onClick={() => chooseRoom('secure')}
                >
                  <LockIcon />
                  Secure
                  {roomSwitch.secureCount > 0 && (
                    <span className={styles.tabCount}>{roomSwitch.secureCount > 99 ? '99+' : roomSwitch.secureCount}</span>
                  )}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={roomSwitch.room === 'telegram'}
                  className={`${styles.tabSegment} ${roomSwitch.room === 'telegram' ? styles.active : ''}`}
                  onClick={() => chooseRoom('telegram')}
                >
                  Telegram
                </button>
              </div>
            ) : (
              <Chips row={roomSwitch} onChoose={choose(roomSwitch)} />
            )}
          </div>
        </div>

        {lensRow && <Chips row={lensRow} onChoose={choose(lensRow)} />}

        <div className={`${styles.thread} ${overlay ? styles.threadUnderTabs : ''}`} ref={threadRef} onScroll={onScroll} data-testid="client-chat-thread">
          <div className={styles.threadInner} ref={innerRef}>
            {status === 'loading' && (
              <div className={styles.stateRow} data-testid="client-chat-loading">
                <div className={styles.spinner} />
              </div>
            )}
            {status === 'not-found' && <div className={styles.stateRow}>{CHAT_NOT_FOUND_LINE}</div>}
            {status === 'error' && (
              <div className={styles.stateRow}>
                Couldn’t load the conversation.
                <button type="button" className={styles.retryBtn} onClick={onRetry}>Try again</button>
              </div>
            )}
            {empty?.kind === 'explainer' && (
              <div className={styles.explain} data-testid="secure-explainer">
                <div className={styles.explainIcon}><LockIcon /></div>
                <h2 className={styles.explainTitle}>Secure messaging</h2>
                <p className={styles.explainBody}>
                  Messages you send here stay between you and {who}. They never pass through Telegram.
                </p>
              </div>
            )}
            {empty?.kind === 'line' && <div className={styles.stateRow}>{empty.text}</div>}
            {status === 'ready' && entries.length > 0 && (
              <ClientChatTimeline
                entries={entries}
                timezone={timezone}
                secureOf={secureOf}
                markOf={markOf}
                onDoor={openDoor}
                openLink={openLink}
                issueOf={issueOf}
                onRetry={onRetrySend}
              />
            )}
          </div>
        </div>

        {typing && (
          <p className={styles.typing} role="status" data-testid="business-typing">{who} is typing…</p>
        )}
        {composer && (
          <SecureComposer
            draftScope={composer.draftScope}
            placeholder={composer.placeholder}
            raised={keyboardOpen}
            onSend={sendMessage}
            onFocus={revealNewest}
            onType={composer.onType}
          />
        )}
        <div className={styles.keyboardSpacer} ref={spacerRef} aria-hidden="true" />
      </div>
    </div>
  );
}
